import React, { useEffect, useState } from "react";
import {
  Box, Typography, TextField, Button, Grid,
  FormControl, InputLabel, Select, MenuItem, OutlinedInput,
} from "@mui/material";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { fetchDepartment, updateDepartment, fetchJobs } from "../../api/companyApi";
import { useNotification } from "../../providers/NotificationProvider";

export default function DepartmentEditPage() {
  const { businessDomainId, businessUnitId, departmentId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });

  const [original, setOriginal] = useState(null);
  const [form, setForm] = useState({ departmentName: "", departmentDescription: "" });
  const [jobs, setJobs] = useState([]);
  const [selectedJobIds, setSelectedJobIds] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [d, jobList] = await Promise.all([
          fetchDepartment(departmentId),
          fetchJobs(businessDomainId),
        ]);
        setOriginal(d);
        setJobs(jobList || []);
        setForm({
          departmentName: d.departmentName || "",
          departmentDescription: d.departmentDescription || "",
        });
        // associatedJobs から初期選択を復元
        if (d.associatedJobs && d.associatedJobs.length > 0) {
          setSelectedJobIds(d.associatedJobs.map((j) => j.jobId));
        } else if (d.jobId) {
          setSelectedJobIds([d.jobId]);
        }
      } catch (e) { notify.apiError(e); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentId, businessDomainId]);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleJobsChange = (e) => {
    const value = e.target.value;
    setSelectedJobIds(typeof value === "string" ? value.split(",") : value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedJobIds.length === 0) {
      notify.error(t("department.job.required"));
      return;
    }
    const selectedJobs = jobs.filter((j) => selectedJobIds.includes(j.jobId));
    try {
      await updateDepartment({
        ...original,
        departmentName: form.departmentName,
        departmentDescription: form.departmentDescription || null,
        jobId: selectedJobs[0]?.jobId,
        jobName: selectedJobs[0]?.jobName,
        associatedJobs: selectedJobs.map((j) => ({ jobId: j.jobId, jobName: j.jobName })),
      });
      notify.success(t("message.update.success"));
      navigate(`/strategy/${businessDomainId}/business-units/${businessUnitId}/departments/${departmentId}`);
    } catch (err) { notify.apiError(err); }
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>{t("department.edit.title")}</Typography>
      <Box component="form" onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField fullWidth required label={t("department.name")} name="departmentName" value={form.departmentName} onChange={handleChange} />
          </Grid>
          <Grid size={12}>
            <FormControl fullWidth required>
              <InputLabel>{t("department.job")}</InputLabel>
              <Select
                multiple
                value={selectedJobIds}
                onChange={handleJobsChange}
                input={<OutlinedInput label={t("department.job")} />}
                renderValue={(selected) =>
                  selected.map((id) => jobs.find((j) => j.jobId === id)?.jobName).filter(Boolean).join("、")
                }
              >
                {jobs.map((j) => (
                  <MenuItem key={j.jobId} value={j.jobId}>{j.jobName}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={12}>
            <TextField fullWidth multiline rows={3} label={t("common.description")} name="departmentDescription" value={form.departmentDescription} onChange={handleChange} />
          </Grid>
        </Grid>
        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="contained" type="submit">{t("common.save")}</Button>
          <Button variant="outlined" onClick={() => navigate(`/strategy/${businessDomainId}/business-units/${businessUnitId}/departments/${departmentId}`)}>
            {t("common.cancel")}
          </Button>
        </Box>
      </Box>
    </Box>
  );
}
